import { useEffect, useMemo, useState } from "react";
import QRCode from "qrcode";
import {
  ensureDepositAddress,
  fetchDepositMe,
  type WalletItem,
} from "@/api/wallets";
import { errorMessage } from "@/api/auth";
import { SelectField, StatusChip } from "@/ui/wx";
import { useToast } from "@/toast/ToastContext";

type Coin = "USDT" | "NMC" | "IRR";

export function DepositCoin({
  token,
  wallets,
  onClose,
  onIrr,
}: {
  token: string;
  wallets: WalletItem[];
  onClose: () => void;
  onIrr: () => void;
}) {
  const toast = useToast();
  const [coin, setCoin] = useState<Coin>("USDT");
  const [net, setNet] = useState("bsc");
  const [addr, setAddr] = useState("");
  const [minUsdt, setMinUsdt] = useState<string>("—");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [qrUrl, setQrUrl] = useState("");

  const nmcWallets = useMemo(
    () => wallets.filter((w) => String(w.chain).toLowerCase() === "nmc"),
    [wallets]
  );
  const bscWallets = useMemo(
    () => wallets.filter((w) => String(w.chain).toLowerCase() === "bsc"),
    [wallets]
  );

  useEffect(() => {
    if (!addr) {
      setQrUrl("");
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(addr, {
      margin: 1,
      width: 148,
      color: { dark: "#0b121e", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setQrUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [addr]);

  useEffect(() => {
    if (coin === "IRR") {
      setAddr("");
      setStatus("");
      return;
    }
    let cancelled = false;
    (async () => {
      setBusy(true);
      setStatus("");
      try {
        if (coin === "USDT") {
          setNet("bsc");
          try {
            const d = await ensureDepositAddress(token);
            if (cancelled) return;
            setAddr(d.depositAddress || "");
            setMinUsdt(d.minDepositUsdt != null ? String(d.minDepositUsdt) : "—");
            if (!d.depositAddress) setStatus("آدرس نیست");
          } catch {
            const me = await fetchDepositMe(token);
            if (cancelled) return;
            setAddr(me.depositAddress || bscWallets[0]?.address || "");
            if (!me.depositAddress && !bscWallets[0]) setStatus("آدرس نیست");
          }
        } else {
          setNet("nmc");
          const a = nmcWallets[0]?.address || "";
          setAddr(a);
          if (!a) setStatus("آدرس نیست");
        }
      } catch (e) {
        if (!cancelled) {
          setStatus(errorMessage(e));
          setAddr("");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [coin, token, nmcWallets, bscWallets]);

  async function copyAddr() {
    if (!addr) return;
    try {
      await navigator.clipboard.writeText(addr);
      toast.ok("کپی");
    } catch {
      toast.err("کپی");
    }
  }

  return (
    <section className="wx-flow" dir="rtl">
      <div className="row-between">
        <h2 className="wx-flow-title">واریز کوین</h2>
        <button type="button" className="wx-chip" onClick={onClose}>
          ✕
        </button>
      </div>

      <SelectField
        label="کوین"
        value={coin}
        onChange={(v) => setCoin(v as Coin)}
        options={[
          { value: "USDT", label: "USDT" },
          { value: "NMC", label: "NMC" },
          { value: "IRR", label: "IRR" },
        ]}
      />

      {coin !== "IRR" && (
        <SelectField
          label="شبکه"
          value={net}
          onChange={setNet}
          options={
            coin === "USDT"
              ? [{ value: "bsc", label: "BSC" }]
              : [{ value: "nmc", label: "NMC" }]
          }
        />
      )}

      {coin === "IRR" ? (
        <button type="button" className="wx-submit buy" onClick={onIrr}>
          واریز ریال
        </button>
      ) : (
        <>
          <div className="wx-alert info">
            <StatusChip tone="muted">وضعیت</StatusChip>
            <span>{busy ? "…" : status || (addr ? "آماده" : "—")}</span>
          </div>
          {coin === "USDT" && (
            <div className="wx-alert warn">
              <span className="ltr">min {minUsdt} USDT · BSC</span>
            </div>
          )}
          <label className="wx-field">
            <span className="wx-label">آدرس</span>
            <div className="wx-addr-box">
              <code className="ltr">{addr || "—"}</code>
              <button type="button" className="wx-chip" disabled={!addr} onClick={() => void copyAddr()}>
                کپی
              </button>
            </div>
          </label>
          <div className="wx-addr-visual" aria-hidden="true">
            {qrUrl ? <img className="wx-addr-qr" src={qrUrl} alt="" /> : <span className="wx-addr-mark">—</span>}
          </div>
        </>
      )}
    </section>
  );
}
